// Variant-aware picker: symmetric filtering on both axes.
//   - Every option value whose combination with the currently-selected
//     other-axis value has no matching variant is hidden entirely
//     (display:none, applied via data-variant-missing).
//   - Every option value whose matching variant EXISTS but is out of
//     stock gets Dawn's `.visually-disabled` class so the swatch picks
//     up Dawn's standard sold-out styling (40% opacity + diagonal X).
//
// On the sleeves product: click 100 stk → only the 6 colors that ship
// in 100 stk remain visible. Click 50 stk → all 15 colors visible.
// Stock status of each visible color reflects that color's variant for
// the current count (not the 50 stk variant's stock).
class VariantAwarePicker extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;

    this.variants = this.parseVariants();
    this.hintEl = this.querySelector('[data-variant-picker-hint]');

    if (this.variants) {
      this.recomputeMissingFlags();
      this.updateContextHint();
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this._unsub = subscribe(PUB_SUB_EVENTS.variantChange, () => {
        if (this.variants) {
          this.recomputeMissingFlags();
          this.updateContextHint();
        }
      });
    }
  }

  disconnectedCallback() {
    if (this._unsub) this._unsub();
  }

  parseVariants() {
    const el = this.querySelector('[data-variant-catalog]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.warn('[variant-aware-picker] catalog JSON parse failed:', e);
      return null;
    }
  }

  recomputeMissingFlags() {
    const selection = this.readCurrentSelection();

    this.querySelectorAll('[data-option-position][data-option-value]').forEach((el) => {
      const position = parseInt(el.dataset.optionPosition, 10);
      const value = el.dataset.optionValue;
      if (!position || value == null) return;

      const idx = position - 1;
      const match = this.variants.find((v) => {
        if (v.options[idx] !== value) return false;
        for (const [pos, val] of Object.entries(selection)) {
          const i = parseInt(pos, 10) - 1;
          if (i === idx) continue;
          if (v.options[i] !== val) return false;
        }
        return true;
      });

      // Reset; the recompute either lands on missing, sold-out, or clean.
      el.removeAttribute('data-variant-missing');
      el.classList.remove('visually-disabled');
      if (el.tagName === 'OPTION') el.removeAttribute('disabled');

      if (!match) {
        el.setAttribute('data-variant-missing', 'true');
        if (el.tagName === 'OPTION') el.setAttribute('disabled', 'disabled');
      } else if (!match.available) {
        // Dawn's .visually-disabled triggers existing 40% opacity + X.
        el.classList.add('visually-disabled');
      }
    });
  }

  readCurrentSelection() {
    const selection = {};
    this.querySelectorAll('fieldset input[type="radio"]:checked').forEach((input) => {
      const pos = parseInt(input.dataset.optionPosition, 10);
      if (pos) selection[pos] = input.dataset.optionValue;
    });
    this.querySelectorAll('select').forEach((select) => {
      const opt = select.options[select.selectedIndex];
      if (!opt) return;
      const pos = parseInt(opt.dataset.optionPosition, 10);
      if (pos) selection[pos] = opt.dataset.optionValue;
    });
    return selection;
  }

  // On 2-axis products, show a line below the picker telling the customer
  // how many primary-axis values (typically colors) are currently
  // available for the selected secondary value (typically count). Example:
  //   "Viser 6 farger tilgjengelig for 100 stk"
  updateContextHint() {
    if (!this.hintEl) return;
    if (!this.variants || this.variants.length < 2) {
      this.hintEl.hidden = true;
      return;
    }

    const valuesByPos = {};
    this.variants.forEach((v) => {
      v.options.forEach((val, i) => {
        const pos = i + 1;
        if (!valuesByPos[pos]) valuesByPos[pos] = new Set();
        valuesByPos[pos].add(val);
      });
    });
    const positions = Object.keys(valuesByPos).map(Number);
    if (positions.length !== 2) {
      this.hintEl.hidden = true;
      return;
    }

    // Primary = axis with most distinct values (Color); secondary = other (Antall).
    positions.sort((a, b) => valuesByPos[b].size - valuesByPos[a].size);
    const primaryPos = positions[0];
    const secondaryPos = positions[1];

    const selection = this.readCurrentSelection();
    const currentSec = selection[secondaryPos];
    if (!currentSec) {
      this.hintEl.hidden = true;
      return;
    }

    // Count distinct primary values that combine with the current secondary.
    const primaries = new Set();
    this.variants.forEach((v) => {
      if (v.options[secondaryPos - 1] === currentSec) primaries.add(v.options[primaryPos - 1]);
    });
    if (!primaries.size) {
      this.hintEl.hidden = true;
      return;
    }

    // Hardcoded Norwegian noun. Override here if reusing the picker for sizes.
    this.hintEl.textContent = `Viser ${primaries.size} farger tilgjengelig for ${currentSec}`;
    this.hintEl.hidden = false;
  }
}

if (!customElements.get('variant-aware-picker')) {
  customElements.define('variant-aware-picker', VariantAwarePicker);
}
